import classNames from 'classnames';
import { inflect, pluralize } from 'inflection';
import * as _ from 'lodash-es';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useVirtualizer } from '@tanstack/react-virtual';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button, { IconButton } from '../components/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import EmptyState from '../components/EmptyState';
import LoadingIndicator from '../components/LoadingIndicator';
import LoadingIndicatorModal from '../components/LoadingIndicatorModal';
import { MenuItem } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import { gql } from '../graphql/generated';
import { UserType } from '../graphql/generated/graphql';
import type { ContentMapColumn_PostsMapFragment } from '../graphql/generated/graphql';
import collapseIcon from '../icons/non-prefilled/16x16/collapse.svg';
import expandIcon from '../icons/non-prefilled/16x16/expand.svg';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import {
  useAllSlimPostsWithoutTopics,
  useRefetchAllSlimPosts,
} from '../post/useAllSlimPosts';
import { UIActions } from '../ui/uiSlice';
import { useBeforeLeaveConfirm } from '../utils/BeforeLeaveConfirm';
import formatBoundedCount from '../utils/formatBoundedCount';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import memoizeDndCollect from '../utils/memoizeDndCollect';
import Link from '../utils/router/Link';
import topicPath from '../utils/url/topicPath';
import usePublicContentPermissions from '../utils/usePublicContentPermissions';
import * as styles from './ContentMap.module.scss';
import ContentMapColumn, { SPECIAL_COLUMN_PADDING } from './ContentMapColumn';
import ContentMapDragLayer from './ContentMapDragLayer';
import ContentMapDroppableSeparator from './ContentMapDroppableSeparator';
import ContentMapFilter from './ContentMapFilter';
import ContentMapNewTopicButton from './ContentMapNewTopicButton';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import {
  DragTypes,
  LOCAL_TOPIC_ID_PREFIX,
  MoveStates,
  TopicStatus,
  UNORGANIZED_TOPIC_ID,
} from './constants';
import NewTopicModal from './modal/NewTopicModal';
import topicPolicyCheck, { TopicPolicyAction } from './topicPolicyCheck';
import useAllTopics, { useRefetchAllTopics } from './useAllTopics';
import useTopicReorganization, {
  createSpecialTopic,
} from './useTopicReorganization';
import type {
  PostEdge,
  TopicForReorganization,
  TopicPosts,
} from './useTopicReorganization';
import type { ContentMapGetDropParams, DragObject } from './utils/types';
import useBatchDataLoader from './utils/useBatchDataLoader';

const ALL_UNORGANIZED_POSTS_QUERY = gql(/** @complexity 8 */ `
  query ContentMapUnorganizedPosts(
    $afterCursor: String
  ) {
    listPosts(first: 100, topicIds: [], after: $afterCursor, sortBy: ALPHABETICAL) {
      edges {
        node {
          id
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const MAX_UNORGANIZED_POSTS = 1000;

const COLUMN_WIDTH = parseInt(styles.columnWidth, 10);
const SEPARATOR_WIDTH = parseInt(styles.separatorWidth, 10);

interface UseAllUnorganizedPostsProps {
  skip: boolean;
  isAllPostsRequired: boolean;
}

const useAllUnorganizedPosts = ({
  skip,
  isAllPostsRequired,
}: UseAllUnorganizedPostsProps) => {
  const response = useQuery(ALL_UNORGANIZED_POSTS_QUERY, {
    skip,
  });

  const fetchMore = ConnectionHelper.generateFetchMore(
    ALL_UNORGANIZED_POSTS_QUERY,
    response,
    'listPosts',
  );

  const { data, loading } = response;

  const posts = useMemo(
    () => _.compact(data?.listPosts?.edges?.map((edge) => edge?.node)),
    [data],
  );

  useEffect(() => {
    if (
      isAllPostsRequired &&
      fetchMore &&
      posts.length < MAX_UNORGANIZED_POSTS &&
      !loading
    ) {
      fetchMore();
    }
  }, [fetchMore, loading, posts.length, isAllPostsRequired]);

  return { posts, loading, refetch: response.refetch };
};

const getTopicIds = (item: DragObject) => item.toMove.topicIds.toReversed();

const POSTS_QUERY = gql(/** @complexity 2 */ `
  query ContentMapPosts($ids: [ID!]!) {
    posts(ids: $ids) {
      id
      ...ContentMapColumn_postsMap
    }
  }
`);

/**
 * A map of post IDs to their corresponding data.
 * `useAllSlimPosts` is used before the map has the data.
 */
const usePostsMapManager = () => {
  const client = useApolloClient();
  const resetIdRef = useRef(0);
  const [postsMap, setPostsMap] = useState<
    Record<string, ContentMapColumn_PostsMapFragment>
  >({});

  const [loadPost, resetBatchLoader] = useBatchDataLoader(async (ids) => {
    const resetId = resetIdRef.current;
    const { data } = await client.query({
      query: POSTS_QUERY,
      variables: { ids },
      fetchPolicy: 'network-only',
    });
    if (resetId !== resetIdRef.current) return;
    const map: typeof postsMap = {};
    data.posts?.forEach((p) => {
      if (p.__typename !== 'Post') return;
      map[p.id] = p;
    });
    setPostsMap((current) => ({ ...current, ...map }));
  });

  const reset = () => {
    resetIdRef.current += 1;
    setPostsMap({});
    resetBatchLoader();
  };

  return [postsMap, loadPost, reset] as const;
};

const TOPIC_POSTS_QUERY = gql(/** @complexity 2 */ `
  query ContentMapTopicPosts($ids: [ID!]!) {
    topics(ids: $ids) {
      id
      ... on Topic {
        pinnedPostIds
      }
    }
  }
`);

/**
 * A map of topic IDs to their corresponding post edges.
 */
const useTopicPostsMapManager = () => {
  const client = useApolloClient();
  const allSlimPosts = useAllSlimPostsWithoutTopics();
  const resetIdRef = useRef(0);

  const [postEdgesMap, setPostEdgesMap] = useState<Record<string, PostEdge[]>>(
    {},
  );

  const cachedTopicPostsMap = useMemo(() => {
    const map: Record<string, Extract<TopicPosts, { status: 'cached' }>> = {};
    allSlimPosts.forEach((post) => {
      post.topics.forEach((topic) => {
        const oldPostIds = map[topic.id]?.postIds ?? [];
        const postIds =
          post.publishedAt && !post.archivedAt
            ? [...oldPostIds, post.id]
            : oldPostIds;

        map[topic.id] = {
          status: 'cached',
          postIds,
        };
      });
    });

    return map;
  }, [allSlimPosts]);

  const [loadPostEdges, resetBatchLoader] = useBatchDataLoader(async (ids) => {
    const resetId = resetIdRef.current;
    const { data } = await client.query({
      query: TOPIC_POSTS_QUERY,
      variables: { ids },
      fetchPolicy: 'network-only',
    });
    if (resetId !== resetIdRef.current) return;
    const map: typeof postEdgesMap = {};
    data.topics?.forEach((t) => {
      if (t.__typename !== 'Topic') return;

      const allPosts = cachedTopicPostsMap[t.id]?.postIds ?? [];
      const edges: PostEdge[] = allPosts
        .map((postId) => {
          const pinnedPostIndex = t.pinnedPostIds.indexOf(postId);
          return {
            postId,
            pinnedOrder: pinnedPostIndex === -1 ? null : pinnedPostIndex,
          };
        })
        .toSorted(
          (a, b) =>
            (a.pinnedOrder ?? allPosts.length) -
            (b.pinnedOrder ?? allPosts.length),
        );
      map[t.id] = edges;
    });
    setPostEdgesMap((current) => ({ ...current, ...map }));
  });

  const topicPostsMap = useMemo(() => {
    const map: Record<string, TopicPosts> = { ...cachedTopicPostsMap };
    Object.entries(postEdgesMap).forEach(([id, edges]) => {
      map[id] = { status: 'loaded', edges };
    });
    return map;
  }, [cachedTopicPostsMap, postEdgesMap]);

  const reset = () => {
    resetIdRef.current += 1;
    setPostEdgesMap({});
    resetBatchLoader();
  };

  return [topicPostsMap, loadPostEdges, reset] as const;
};

const useContentMapData = (options: {
  isShowingAllTopics: boolean;
  isShowingUnorganizedColumn: boolean;
}) => {
  const [postsMap, loadPost, resetPostsMap] = usePostsMapManager();
  const [topicPostsMap, loadPostEdges, resetTopicPostsMap] =
    useTopicPostsMapManager();

  const allTopics = useAllTopics();
  const flattenedAllTopics = useAllTopics({ flatten: true });
  const refetchAllTopics = useRefetchAllTopics();
  const refetchAllSlimPosts = useRefetchAllSlimPosts();

  const refetchTopicsAndPostsData = async () => {
    await Promise.all([refetchAllTopics(), refetchAllSlimPosts()]);
    resetPostsMap();
    resetTopicPostsMap();
  };

  const unorganizedPostsResult = useAllUnorganizedPosts({
    skip: !options.isShowingAllTopics,
    isAllPostsRequired: options.isShowingUnorganizedColumn,
  });

  const unorganizedPosts = unorganizedPostsResult.posts;
  const unorganizedPostCount = unorganizedPostsResult.loading
    ? null
    : formatBoundedCount(unorganizedPosts.length, 99);

  return {
    postsMap,
    loadPost,
    topicPostsMap,
    loadPostEdges,
    allTopics,
    flattenedAllTopics,
    unorganizedPosts,
    unorganizedPostCount,
    refetchUnorganizedPostCount: unorganizedPostsResult.refetch,
    refetchTopicsAndPostsData,
  };
};

interface ContentMapProps {
  topicId?: string;
}

const ContentMap = ({ topicId }: ContentMapProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const isShowingAllTopics = !topicId;
  const [reorganizeModeTopics, setReorganizeModeTopics] = useState<
    TopicForReorganization[] | null
  >(null);
  const isReorganizing = !!reorganizeModeTopics;
  const isShowingUnorganizedColumn = isReorganizing && isShowingAllTopics;

  const {
    postsMap,
    loadPost,
    topicPostsMap,
    loadPostEdges,
    allTopics,
    flattenedAllTopics,
    unorganizedPosts,
    unorganizedPostCount,
    refetchTopicsAndPostsData,
    refetchUnorganizedPostCount,
  } = useContentMapData({ isShowingAllTopics, isShowingUnorganizedColumn });

  const [isAllExpanded, setIsAllExpanded] = useState(true);
  const [isShowingPosts, setIsShowingPosts] = useState(true);
  const [visibleColumnTopicIds, setVisibleColumnTopicIds] = useState(
    new Set<string>(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const boardRef = useRef<HTMLDivElement | null>(null);

  const { isPermittedToCreatePublicSubtopic } = usePublicContentPermissions();

  const topic = useMemo(
    () => flattenedAllTopics.find(({ id }) => id === topicId),
    [flattenedAllTopics, topicId],
  );
  const normalModeTopics = topic ? topic.childTopics : allTopics;

  const topics = reorganizeModeTopics || normalModeTopics;

  const {
    tree,
    selectedPosts,
    changeCount,
    reset,
    save,
    setPostSelected,
    setPostDeleted,
    setPostPinned,
    setPostArchived,
    setTopicSelected,
    setTopicName,
    setTopicDeleted,
    createSubtopic,
    moveTopic,
    movePost,
    movePinnedPost,
  } = useTopicReorganization({
    topic,
    topics: useMemo(
      () =>
        isShowingUnorganizedColumn
          ? [
              ...topics,
              createSpecialTopic({
                id: UNORGANIZED_TOPIC_ID,
                name: 'Unorganized posts',
              }),
            ]
          : topics,
      [isShowingUnorganizedColumn, topics],
    ),
    topicPostsMap: useMemo(
      () =>
        isShowingUnorganizedColumn
          ? {
              ...topicPostsMap,
              [UNORGANIZED_TOPIC_ID]: {
                status: 'loaded',
                edges: unorganizedPosts.map((post) => ({
                  postId: post.id,
                  pinnedOrder: null,
                })),
              },
            }
          : topicPostsMap,
      [isShowingUnorganizedColumn, topicPostsMap, unorganizedPosts],
    ),
  });

  useBeforeLeaveConfirm({ skip: !changeCount });

  const handleSave = async () => {
    dispatch(UIActions.openModal(LoadingIndicatorModal));
    setIsSaving(true);
    await save();
    await refetchTopicsAndPostsData();
    reset();
    handleUpdateReorganizing(false);
    // Refetch unorganized post count only after "handleUpdateReorganizing" so that
    // it doesn't interfere with the unorganized posts column
    refetchUnorganizedPostCount();
    setIsSaving(false);
    setVisibleColumnTopicIds(
      new Set(
        [...visibleColumnTopicIds].filter(
          (id) => !id.startsWith(LOCAL_TOPIC_ID_PREFIX),
        ),
      ),
    );

    dispatch(UIActions.closeModal());
  };

  const getDropParams = useCallback<ContentMapGetDropParams>(
    (
      accept,
      {
        destinationTree,
        indexWithPlaceholder,
        pinnedPostIndexWithPlaceholder,
        canDrop = () => true,
      },
    ) => ({
      accept,
      drop: (item) => {
        const topicIds = getTopicIds(item);
        const postKeys =
          item.type === DragTypes.column
            ? []
            : item.toMove.postKeys.toReversed();
        const pinnedPostKeys =
          item.type === DragTypes.column
            ? []
            : item.toMove.pinnedPostKeys.toReversed();
        const destinationId = destinationTree.topic.id;

        if (typeof indexWithPlaceholder === 'number') {
          topicIds.forEach((id) => {
            moveTopic({ id, destinationId, indexWithPlaceholder });
            setTopicSelected({ id, selected: false });
          });
        }

        postKeys.forEach((postKey) => {
          movePost({ postKey, destinationId });
          setPostSelected({ postKey, selected: false });
        });

        if (typeof pinnedPostIndexWithPlaceholder === 'number') {
          pinnedPostKeys.forEach((postKey) => {
            movePinnedPost({
              postKey,
              destinationId,
              indexWithPlaceholder: pinnedPostIndexWithPlaceholder,
            });
            setPostSelected({ postKey, selected: false });
          });
        }

        if (visibleColumnTopicIds.size > 0) {
          if (
            item.type !== DragTypes.column &&
            destinationTree.topic.id === topicId
          ) {
            setVisibleColumnTopicIds(
              new Set([...visibleColumnTopicIds, ...topicIds]),
            );
          } else if (
            item.type === DragTypes.column &&
            destinationTree.topic.id !== topicId
          ) {
            const newSelectedIds = new Set(visibleColumnTopicIds);
            topicIds.forEach((t) => newSelectedIds.delete(t));
            setVisibleColumnTopicIds(newSelectedIds);
          }
        }
      },
      canDrop: (item) =>
        accept.includes(item.type) &&
        destinationTree.reorganizationMetadata.move.state !==
          MoveStates.movedOut &&
        getTopicIds(item).every(
          (id) =>
            id !== destinationTree.topic.id &&
            destinationTree.topic.ancestors.every(
              (ancestor) => ancestor.id !== id,
            ),
        ) &&
        canDrop(item),
      collect: memoizeDndCollect((monitor) => ({
        isOver: monitor.canDrop() && monitor.isOver({ shallow: true }),
        isDragging: !!monitor.getItemType(),
      })),
    }),
    [
      movePinnedPost,
      movePost,
      moveTopic,
      setPostSelected,
      setTopicSelected,
      topicId,
      visibleColumnTopicIds,
    ],
  );

  const handleUpdateReorganizing = useCallback(
    (newIsReorganizing: boolean) => {
      setReorganizeModeTopics(newIsReorganizing ? normalModeTopics : null);
    },
    [normalModeTopics],
  );

  const handleToggleExpand = () => setIsAllExpanded(!isAllExpanded);

  const handleCreateTopic = useCallback(
    (reorganizeMode: boolean) => {
      const parentId = topicId;
      if (reorganizeMode) {
        const topicTempId = _.uniqueId(LOCAL_TOPIC_ID_PREFIX);
        createSubtopic({ topicTempId, parentId: parentId ?? null });
        if (!parentId) {
          setVisibleColumnTopicIds((currentIds) =>
            currentIds.size > 0
              ? new Set([...currentIds, topicTempId])
              : currentIds,
          );
        }
        handleUpdateReorganizing(true);
      } else {
        dispatch(UIActions.openModal(NewTopicModal, { parentId }));
      }
    },
    [dispatch, topicId, createSubtopic, handleUpdateReorganizing],
  );
  const createNewNoun = isShowingAllTopics ? 'topic' : 'subtopic';

  const columnTrees = useMemo(
    () =>
      tree.children.filter(
        ({ reorganizationMetadata }) =>
          reorganizationMetadata.move.state !== MoveStates.movedOut,
      ),
    [tree],
  );

  const columns = useMemo(() => {
    const selectedColumnTrees =
      visibleColumnTopicIds.size > 0
        ? columnTrees.filter(({ topic: { id } }) =>
            visibleColumnTopicIds.has(id),
          )
        : columnTrees;

    const sharedColumnProps = {
      allExpanded: isAllExpanded,
      selectedPosts,
      getDropParams,
      showPosts: isShowingPosts,
      onPostSelectedChange: setPostSelected,
      onPostDeletedChange: setPostDeleted,
      onPostPinnedChange: setPostPinned,
      onPostArchivedChange: setPostArchived,
      onTopicSelectedChange: setTopicSelected,
      onTopicDeletedChange: setTopicDeleted,
      onTopicNameChange: setTopicName,
      onSubtopicCreate: createSubtopic,
    };
    const result = selectedColumnTrees.map((columnTree) => ({
      key: columnTree.topic.id,
      render: (style: CSSProperties) => (
        <div style={style} className={styles.column}>
          <ContentMapDroppableSeparator
            {...getDropParams([DragTypes.column, DragTypes.topics], {
              destinationTree: tree,
              indexWithPlaceholder: tree.children.indexOf(columnTree),
            })}
          />
          <ContentMapColumn
            isReorganizing={isReorganizing}
            tree={columnTree}
            specialColumn={
              columnTree.topic.id === UNORGANIZED_TOPIC_ID
                ? 'unorganized'
                : null
            }
            loadPostEdges={loadPostEdges}
            postsMap={postsMap}
            loadPost={loadPost}
            {...sharedColumnProps}
          />
        </div>
      ),
      width:
        COLUMN_WIDTH +
        SEPARATOR_WIDTH +
        (columnTree.topic.id === UNORGANIZED_TOPIC_ID
          ? SPECIAL_COLUMN_PADDING
          : 0),
    }));

    if (
      topic &&
      isShowingPosts &&
      ((tree.reorganizationTopicPosts.status === 'loaded' &&
        tree.reorganizationTopicPosts.edges.length > 0) ||
        (tree.reorganizationTopicPosts.status === 'cached' &&
          tree.reorganizationTopicPosts.postIds.length > 0))
    ) {
      result.unshift({
        key: 'posts',
        render: (style) => (
          <div style={style} className={styles.column}>
            <ContentMapDroppableSeparator accept={[]} />
            <ContentMapColumn
              isReorganizing={isReorganizing}
              specialColumn="posts"
              tree={{
                topic: { ...topic, name: 'Posts' },
                children: [],
                isParentSelected: false,
                isSelected: false,
                reorganizationTopicPosts: tree.reorganizationTopicPosts,
                reorganizationMetadata: {
                  move: { state: MoveStates.initial },
                  status: TopicStatus.initial,
                },
                selected: tree.selected,
              }}
              loadPostEdges={loadPostEdges}
              postsMap={postsMap}
              loadPost={loadPost}
              {...sharedColumnProps}
            />
          </div>
        ),
        width: COLUMN_WIDTH + SEPARATOR_WIDTH + SPECIAL_COLUMN_PADDING,
      });
    }

    const isLastUnorganized = _.last(result)?.key === UNORGANIZED_TOPIC_ID;

    result.push({
      key: 'empty',
      render: (style) => (
        <div style={style} className={styles.column}>
          <ContentMapDroppableSeparator
            {...getDropParams(
              isLastUnorganized ? [] : [DragTypes.column, DragTypes.topics],
              {
                destinationTree: tree,
                indexWithPlaceholder: tree.children.length,
              },
            )}
          />
          <div className={styles.createColumn}>
            <EmptyState
              imageName="connections"
              title={`Create a new ${createNewNoun}`}
              message={`Use additional ${pluralize(
                createNewNoun,
              )} to organize posts and other ${pluralize(createNewNoun)}.`}
              action={
                <ContentMapNewTopicButton
                  topic={topic}
                  isPermittedToCreatePublicSubtopic={
                    isPermittedToCreatePublicSubtopic
                  }
                  onCreateTopic={handleCreateTopic}
                />
              }
              vertical
              smallIcon
              smallText
            />
          </div>
        </div>
      ),
      width: COLUMN_WIDTH + SEPARATOR_WIDTH,
    });

    return result;
  }, [
    isAllExpanded,
    handleCreateTopic,
    createNewNoun,
    getDropParams,
    isReorganizing,
    isShowingPosts,
    visibleColumnTopicIds,
    setPostSelected,
    setPostDeleted,
    setPostPinned,
    setPostArchived,
    setTopicSelected,
    setTopicDeleted,
    setTopicName,
    createSubtopic,
    tree,
    topic,
    columnTrees,
    isPermittedToCreatePublicSubtopic,
    selectedPosts,
    loadPostEdges,
    postsMap,
    loadPost,
  ]);

  const columnVirtualizer = useVirtualizer({
    count: columns.length,
    horizontal: true,
    getScrollElement: () => boardRef.current,
    estimateSize: (i) => columns[i]!.width,
    getItemKey: (i) => columns[i]!.key,
  });

  useEffect(() => {
    if (topicId) {
      loadPostEdges(topicId);
    }
  }, [topicId, loadPostEdges]);

  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  if (topicId && !topic) return null;

  const desktopToolbar = (
    <div className={classNames(styles.toolbar, styles.desktop)}>
      {isShowingAllTopics && unorganizedPostCount ? (
        <Button
          variant="link"
          to="/unorganized"
          newTab
          className={styles.unorganized}
        >
          Unorganized ({unorganizedPostCount})
        </Button>
      ) : null}
      <Button
        icon={postIcon}
        variant="secondary"
        onClick={() => setIsShowingPosts(!isShowingPosts)}
        size="small"
      >
        {isShowingPosts ? 'Hide posts' : 'Show posts'}
      </Button>
      <Button
        icon={isAllExpanded ? collapseIcon : expandIcon}
        variant="secondary"
        onClick={handleToggleExpand}
        size="small"
      >
        {isAllExpanded ? 'Collapse' : 'Expand'}
      </Button>
      {(currentUser.type === UserType.Admin ||
        (topic &&
          topicPolicyCheck(
            TopicPolicyAction.ReorganizeTopic,
            topic,
            currentUser,
          ))) &&
        columns.length > 0 && (
          <Button
            variant="primary"
            onClick={() => handleUpdateReorganizing(!isReorganizing)}
            size="small"
            disabled={isReorganizing}
          >
            Reorganize
          </Button>
        )}
    </div>
  );

  const mobileToolbar = (
    <div className={classNames(styles.toolbar, styles.mobile)}>
      <IconButton
        size="md"
        aria-label="Show posts"
        onClick={() => setIsShowingPosts(!isShowingPosts)}
      >
        {postIcon}
      </IconButton>
      <IconButton
        size="md"
        aria-label={isAllExpanded ? 'Collapse' : 'Expand'}
        onClick={handleToggleExpand}
      >
        {isAllExpanded ? collapseIcon : expandIcon}
      </IconButton>
      {isShowingAllTopics && unorganizedPostCount ? (
        <OverflowMenu
          open={isOverflowMenuOpen}
          onOpenChange={setIsOverflowMenuOpen}
          button={
            <IconButton
              active={isOverflowMenuOpen}
              aria-label="Options"
              size="md"
            >
              {overflowMenuIcon}
            </IconButton>
          }
        >
          <MenuItem to="/unorganized" icon={postIcon} newTab>
            Unorganized posts ({unorganizedPostCount})
          </MenuItem>
        </OverflowMenu>
      ) : null}
    </div>
  );

  let header = null;
  if (isShowingAllTopics) {
    header = <h2>Content Map</h2>;
  } else if (topic) {
    header = (
      <div>
        <div>
          <TopicBreadcrumbs
            topics={topic.ancestors}
            link
            showRoot
            tab="subtopics"
          />
        </div>
        <Link to={topicPath(topic)}>
          <h2>{topic.name}</h2>
        </Link>
      </div>
    );
  }

  if (!header) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {header}
          {columnTrees.length > 0 && (
            <div className={styles.filter}>
              <ContentMapFilter
                columnTrees={columnTrees}
                selectedIds={visibleColumnTopicIds}
                onSelectedIdsChange={setVisibleColumnTopicIds}
              />
            </div>
          )}
        </div>
        {desktopToolbar}
        {mobileToolbar}
      </div>
      {columns.length > 1 ? (
        <div className={styles.topics} ref={boardRef}>
          {isReorganizing && <ContentMapDragLayer />}
          <div
            className={styles.listInner}
            style={{ width: columnVirtualizer.getTotalSize() }}
          >
            {columnVirtualizer
              .getVirtualItems()
              .map(({ index, key, start, size }) => {
                const style: CSSProperties = {
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: `translateX(${start}px)`,
                  width: size,
                  height: '100%',
                };
                return (
                  <React.Fragment key={key}>
                    {columns[index]!.render(style)}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      ) : (
        <EmptyState
          imageName="connections"
          title={`No ${pluralize(createNewNoun)} yet…`}
          message={
            isShowingAllTopics
              ? 'Your topic tree will appear after new topics are created.'
              : 'Your topic tree will appear after new subtopics are created under this topic.'
          }
          action={
            <ContentMapNewTopicButton
              size="large"
              topic={topic}
              isPermittedToCreatePublicSubtopic={
                isPermittedToCreatePublicSubtopic
              }
              onCreateTopic={handleCreateTopic}
            />
          }
        />
      )}
      {isReorganizing && (
        <div className={styles.footer}>
          <div className={styles.info}>
            {changeCount > 0 ? changeCount : 'No'} unsaved{' '}
            {inflect('change', changeCount)}
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              handleUpdateReorganizing(false);
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              dispatch(
                UIActions.openModal(ConfirmationModal, {
                  title: 'Save changes?',
                  description: `You have made ${changeCount} ${inflect(
                    'change',
                    changeCount,
                  )} to your team’s topic and post hierarchy.`,
                  confirmButton: {
                    text: 'Save',
                    onClick: handleSave,
                  },
                }),
              )
            }
            disabled={changeCount === 0 || isSaving}
          >
            {isSaving ? <LoadingIndicator adaptColor delay={0} /> : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ContentMap;
